// Pure helpers for F-F media + narration playback. Shared by the staff CMS
// (per-kind media tabs) and the visitor app (media list + narration
// precedence). No React, no I/O — just the language-filter rule and the
// narration source-precedence rule, so both surfaces agree and both are
// unit-testable.
//
// Wire format is camelCase per ADR-0020 — the entity fields read here are
// `narrationAssetId<Lang>` / `voiceUrl<Lang>` / `media[].language`.

import type { ContentLanguage } from "@shared/lib/language";

// Alias of the canonical ContentLanguage (was a separate `en`-first union).
export type NarrationLanguage = ContentLanguage;

export interface MediaLike {
  language?: string | null;
}

// Visitor/staff filter rule: a specific language shows items tagged with that
// language PLUS universal (null/empty) items; "all" shows everything. Universal
// items always appear so a language-neutral document/track is never hidden.
export function filterMediaByLanguage<T extends MediaLike>(
  media: T[],
  language: string,
): T[] {
  if (language === "all") return media;
  return media.filter((m) => !m.language || m.language === language);
}

export interface Narratable {
  narrationAssetIdEn?: string | null;
  narrationAssetIdTc?: string | null;
  narrationAssetIdSc?: string | null;
  voiceUrlEn?: string | null;
  voiceUrlTc?: string | null;
  voiceUrlSc?: string | null;
}

export type NarrationSource =
  | { type: "asset"; assetId: string }
  | { type: "voice"; url: string }
  | { type: "tts" };

// camelCase field keys per ADR-0020, by language.
const NARRATION_KEY: Record<NarrationLanguage, keyof Narratable> = {
  en: "narrationAssetIdEn",
  tc: "narrationAssetIdTc",
  sc: "narrationAssetIdSc",
};
const VOICE_KEY: Record<NarrationLanguage, keyof Narratable> = {
  en: "voiceUrlEn",
  tc: "voiceUrlTc",
  sc: "voiceUrlSc",
};

// Precedence (F-F Q-1): stored narration asset → legacy voiceUrl → browser
// TTS. Returns the winning source so the caller can build the playback URL
// (asset → `${base}/assets/${assetId}`) or fall back to speech synthesis.
export function narrationSource(
  entity: Narratable,
  language: NarrationLanguage,
): NarrationSource {
  const assetId = entity[NARRATION_KEY[language]];
  if (assetId) return { type: "asset", assetId };
  const voice = entity[VOICE_KEY[language]];
  if (voice) return { type: "voice", url: voice };
  return { type: "tts" };
}
